import {agentTaskQueryKey, fetchTask} from '@github-ui/agent-sessions/hooks/use-task'
import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import {isSessionState, type SessionState} from '@github-ui/agent-sessions/types/session'
import {getAPITaskState, getAPITaskUpdatedAt} from '@github-ui/agent-sessions/utils/task-helpers'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQueries, useQueryClient} from '@tanstack/react-query'
import {useEffect, useMemo} from 'react'

import type {AgentTask} from '../../api/memex-items/contracts'
import {getVisibleAgentTasks, isUnhydratedAgentTask} from '../../components/table/agent-task-utils'
import type {MemexItemModel} from '../../models/memex-item-model'
import {updateAgentTasksForItemInQueryClient} from '../../state-providers/memex-items/query-client-api/memex-items/update-items'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'

/**
 * A unique unhydrated agent task to observe, along with every item that
 * references it (the same task id can appear on more than one item).
 */
interface TaskObservation {
  taskId: string
  owner: string
  repo: string
  itemIds: Set<number>
}

/** State resolved from a fetched CAPI task, ready to merge into the cache. */
interface HydratedTaskFields {
  state: SessionState
  title?: string
  createdAt?: string
  lastUpdatedAt?: string
}

/**
 * Resolves the `owner`/`repo` for a task, preferring the task's `repoNwo`
 * (which the backend always sends) and falling back to the item's
 * issue-derived name-with-owner.
 */
function resolveOwnerRepo(task: AgentTask, model: MemexItemModel): {owner: string; repo: string} | undefined {
  if (task.repoNwo) {
    const [owner, repo] = task.repoNwo.split('/')
    if (owner && repo) {
      return {owner, repo}
    }
  }

  const nameWithOwner = model.getNameWithOwner?.()
  if (nameWithOwner) {
    const [owner, repo] = nameWithOwner
    if (owner && repo) {
      return {owner, repo}
    }
  }

  return undefined
}

/**
 * Collects the unique unhydrated agent tasks worth observing across the loaded
 * items. Scoped to the *visible* tasks per item (`getVisibleAgentTasks`, capped
 * at MAX_AGENT_TASK_SUB_ROWS) so we never fetch state for tasks that aren't
 * rendered — a large board with many tasks per item would otherwise fire a
 * fetch storm.
 */
function collectTaskObservations(items: Readonly<Array<MemexItemModel>>): Array<TaskObservation> {
  const byTaskId = new Map<string, TaskObservation>()

  for (const model of items) {
    for (const task of getVisibleAgentTasks(model, true)) {
      if (!task.id || !isUnhydratedAgentTask(task)) continue

      const existing = byTaskId.get(task.id)
      if (existing) {
        existing.itemIds.add(model.id)
        continue
      }

      const ownerRepo = resolveOwnerRepo(task, model)
      if (!ownerRepo) continue

      byTaskId.set(task.id, {
        taskId: task.id,
        owner: ownerRepo.owner,
        repo: ownerRepo.repo,
        itemIds: new Set([model.id]),
      })
    }
  }

  return [...byTaskId.values()]
}

/**
 * Pure transform: writes freshly fetched state (and any newly available
 * metadata) onto the matching task, clearing a prior `hydrationFailed` marker.
 * Every other task on the item is left untouched.
 */
export function setHydratedState(
  tasks: Array<AgentTask>,
  taskId: string,
  fields: HydratedTaskFields,
): Array<AgentTask> {
  return tasks.map(task => {
    if (task.id !== taskId) return task

    // Drop the transient `hydrationFailed` flag now that we have real state.
    const {hydrationFailed: _hydrationFailed, ...rest} = task
    return {
      ...rest,
      state: fields.state,
      title: fields.title ?? task.title,
      createdAt: fields.createdAt ?? task.createdAt,
      lastUpdatedAt: fields.lastUpdatedAt ?? task.lastUpdatedAt,
    }
  })
}

/**
 * Pure transform: marks the matching task as having failed hydration so the
 * render path hides it instead of showing a stuck loading pill. Returns the
 * same array contents when the task is already marked (so the cache write is a
 * no-op).
 */
export function markHydrationFailed(tasks: Array<AgentTask>, taskId: string): Array<AgentTask> {
  return tasks.map(task => {
    if (task.id !== taskId || task.hydrationFailed) return task
    return {...task, hydrationFailed: true}
  })
}

function resolveHydratedFields(apiTask: HydratedAPITask): HydratedTaskFields | undefined {
  const rawState = getAPITaskState(apiTask)
  if (!isSessionState(rawState)) return undefined

  return {
    state: rawState,
    title: apiTask.name,
    createdAt: apiTask.created_at,
    lastUpdatedAt: getAPITaskUpdatedAt(apiTask),
  }
}

/**
 * Lazy-loads the state of agent assignment tasks on the client.
 *
 * When `memex_lazy_hydrate_agent_tasks` is enabled, the backend may send agent
 * tasks with an `id` and agent metadata but no `state` (the "unhydrated"
 * window). This hook observes those tasks with React Query (reusing
 * `@github-ui/agent-sessions`'s `fetchTask` and its shared cache) and merges the
 * resolved state back into the cached item so the board/table/roadmap pills can
 * render the real status.
 *
 * Using an observer (`useQueries`) rather than a one-shot fetch gives us, for
 * free: retry/backoff on transient failures; a loading-vs-terminal-failure
 * distinction (so we can show a loading pill while retrying but hide a task that
 * permanently fails); and natural re-hydration when memex's live updates refetch
 * an item and the backend re-sends it unhydrated.
 *
 * It is a complete no-op when the flag is off or when there are no unhydrated
 * tasks (i.e. while the backend is still hydrating state server-side), so it is
 * safe to merge and roll out independently of the backend change.
 */
export function useHydrateAgentTasks(): void {
  const lazyHydrationEnabled = useFeatureFlag('memex_lazy_hydrate_agent_tasks')
  const queryClient = useQueryClient()
  const {items} = useMemexItems()

  const observations = useMemo<Array<TaskObservation>>(() => {
    if (!lazyHydrationEnabled) return []
    return collectTaskObservations(items)
  }, [lazyHydrationEnabled, items])

  const results = useQueries({
    queries: observations.map(({owner, repo, taskId}) => ({
      queryKey: agentTaskQueryKey(owner, repo, taskId),
      queryFn: ({signal}: {signal?: AbortSignal}) => fetchTask({taskId, signal}),
      enabled: lazyHydrationEnabled,
    })),
  })

  // A stable signature of the per-task observation status so the write-back
  // effect only runs when something actually resolved (or failed), not on every
  // render. `success`/`error` are terminal for our purposes; while a fetch is
  // retrying the status stays `pending`. The referencing `itemIds` are part of
  // the signature so that when an already-resolved task gains a *new* item (the
  // same task can appear on more than one item, and a later-loading item won't
  // change the task's status or `dataUpdatedAt`) the effect re-runs and merges
  // the cached state into that new item too. Re-running for already-merged items
  // is safe — the write-back is idempotent (guarded by `isEqual`).
  const writeBackSignature = results
    .map((result, index) => {
      const observation = observations[index]
      const itemIds = observation ? [...observation.itemIds].sort((a, b) => a - b).join(',') : ''
      return `${observation?.taskId}:${result.status}:${result.dataUpdatedAt}:${itemIds}`
    })
    .join('|')

  useEffect(() => {
    if (!lazyHydrationEnabled) return

    for (let index = 0; index < observations.length; index++) {
      const observation = observations[index]
      const result = results[index]
      if (!observation || !result) continue

      if (result.status === 'success' && result.data) {
        const fields = resolveHydratedFields(result.data)
        if (!fields) continue

        for (const itemId of observation.itemIds) {
          updateAgentTasksForItemInQueryClient(queryClient, itemId, tasks =>
            setHydratedState(tasks, observation.taskId, fields),
          )
        }
      } else if (result.status === 'error') {
        for (const itemId of observation.itemIds) {
          updateAgentTasksForItemInQueryClient(queryClient, itemId, tasks =>
            markHydrationFailed(tasks, observation.taskId),
          )
        }
      }
    }
    // `observations`/`results` are captured from the current render; the
    // signature changes whenever a status or data version does, which is exactly
    // when we want to re-run the write-back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyHydrationEnabled, queryClient, writeBackSignature])
}

/**
 * Headless host for {@link useHydrateAgentTasks}. Rendered once near the top of
 * the project view so the React Query observers it owns re-render this tiny
 * component (which returns `null`) rather than the much larger view tree.
 */
export function AgentTaskHydrator(): null {
  useHydrateAgentTasks()
  return null
}
